import { useState, useEffect, useReducer, useMemo } from "react";
import { CustomTooltip } from "../nexus/nexus.js";
import FindInPageTwoToneIcon from "@mui/icons-material/FindInPageTwoTone";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import { IosShare } from "@mui/icons-material";
import {
  sortAlpha,
  sortDigitsAlpha,
  setUnique,
  finderSelectStyles,
  exportToExcel,
} from "../nexus/finder.js";
import useColumnsNote from "../hooks/useColumnsNote.js";
import { HiSortAscending, HiSortDescending } from "react-icons/hi";
import DatePicker from "react-datepicker";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  useBlockLayout,
  useResizeColumns,
} from "react-table";

import finderStyles from "../styles/finder.module.css";
import VisibilityOff from "../scenes/VisibilityOff.js";
import VisibilityOn from "../scenes/VisibilityOn.js";
import { BiReset } from "react-icons/bi";
import { RiArrowDownSLine, RiArrowRightSLine } from "react-icons/ri";
import axios from "axios";
import moment from "moment";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { ThreeDots } from "react-loader-spinner";
export default function NoteFinder() {
  const [notes, setNotes] = useState([]);
  const [columns, setColumns] = useState(useColumnsNote());
  const userToken = useSelector((state) => state.userToken);

  const filterInitialState = {
    isOpen: {
      id: false,
      title: false,
      comment: false,
      report: false,
    },
    noteIds: [],
    titles: [],
    comments: [],
    reports: [],
    id: [],
    title: [],
    comment: [],
    report: [],
    modFrom: null,
    modTo: null,
  };

  const reducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
      case "SET_IS_OPEN":
        return {
          ...state,
          isOpen: {
            ...state.isOpen,
            [payload.type]: payload.value,
          },
        };
      case "RESET_FILTERS":
        const { comments, reports, titles, noteIds, ...filteredInitialState } =
          filterInitialState;
        return {
          ...state,
          ...filteredInitialState,
        };

      default:
        return { ...state, [type]: payload };
    }
  };
  const [filterState, dispatch] = useReducer(reducer, filterInitialState);
  const {
    isOpen,
    noteIds,
    titles,
    comments,
    reports,
    id,
    title,
    comment,
    report,
    modFrom,
    modTo,
  } = filterState;
  const columnMap = {
    id: { label: "ID", width: 10 },
    title: { label: "Title", width: 25 },
    comment: { label: "Comment", width: 40 },
    report: { label: "Report", width: 15 },
    desc: { label: "Description", width: 40 },
    lastModifiedDate: { label: "Date Modified", width: 15 },
  };
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (isLoggedIn === null || isLoggedIn !== "true") {
      window.location.href = "/";
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get("/getAllNotes", {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });

      const modifiedItems = response.data
        .map((item) => {
          const deletedReports = new Set(item.deletedReports || []);
          const filteredReports = item.reports
            ? item.reports.filter((report) => !deletedReports.has(report))
            : [];

          // If no valid reports remain, exclude this item
          if (filteredReports.length === 0) {
            return null;
          }

          return {
            id: item.id,
            title: item.title,
            desc: item.desc,
            report: filteredReports.join(", "), // Join reports as a string

            // Filter out comments where the first part before '-' exists in deletedReports
            comment: item.comments
              ? item.comments
                  .filter((comment) => {
                    const commentPrefix = comment.split("-")[0].trim();
                    return !deletedReports.has(commentPrefix);
                  })
                  .join(", ")
              : "", // Empty string if no comments

            // Format lastModifiedDate
            lastModifiedDate: item.lastModified
              ? moment(item.lastModified.split(" ")[0], "D/M/YYYY").format(
                  "DD/MM/YYYY"
                )
              : "", // Empty string if no lastModified
          };
        })
        .filter(Boolean); // Remove null items from the array

      setNotes(modifiedItems);
      setFilterData(modifiedItems);
      sessionStorage.setItem("noteItems", JSON.stringify(modifiedItems));
    } catch (error) {
      console.error("Error while retrieving notes:", error);
    }
  };

  const setFilterData = (items) => {
    const titles = sortAlpha(setUnique(items, "title"));
    dispatch({ type: "titles", payload: titles });

    const noteIds = sortAlpha(setUnique(items, "id"));
    dispatch({ type: "noteIds", payload: noteIds });

    const comments = sortDigitsAlpha(
      items.flatMap((item) => item.comment.split(",").map((r) => r.trim()))
    ).map((r) => ({ label: r, value: r }));

    dispatch({ type: "comments", payload: comments });

    const reports = sortDigitsAlpha(
      items.flatMap((item) => item.report.split(",").map((r) => r.trim()))
    ).map((r) => ({ label: r, value: r }));

    dispatch({ type: "reports", payload: reports });
  };

  const data = useMemo(() => notes, [notes]);
  const defaultColumn = useMemo(
    () => ({
      minWidth: 30,
      width: 300,
      maxWidth: 400,
    }),
    []
  );
  const tableInstance = useTable(
    {
      columns,
      initialState: {
        hiddenColumns: columns.map((column) => {
          if (column.show === false) return column.accessor || column.id;
        }),
        sortBy: [
          {
            id: "lastModifiedDate",
            desc: true,
          },
        ],
      },
      data,
      defaultColumn,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useBlockLayout,
    useResizeColumns
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setFilter,
    state,
    setGlobalFilter,
    setAllFilters,
  } = tableInstance;

  const { globalFilter } = state;

  const handleSearch = (e) => {
    setGlobalFilter(e.target.value || undefined);
  };

  const handleHide = (accessor) => {
    setColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.accessor === accessor
          ? { ...column, show: !column.show }
          : column
      )
    );
  };

  const handleRangeValues = (value, prevFilters, isChecked) => {
    const [start, end] = value.split("-");
    const lowerBound = parseInt(start);
    const upperBound = parseInt(end);
    const rangeValues = [];

    for (let i = lowerBound; i <= upperBound; i++) {
      rangeValues.push(i.toString());
    }

    return isChecked
      ? [...prevFilters, ...rangeValues]
      : prevFilters.filter((value) => !rangeValues.includes(value));
  };

  const handleSelect = (columnId, e) => {
    const filters = (prevFilters = []) => {
      const value = e.target.value;
      const isChecked = e.target.checked;

      if (value.includes("-")) {
        return handleRangeValues(value, prevFilters, isChecked);
      }

      return isChecked
        ? [...prevFilters, value]
        : prevFilters.filter((filter) => filter !== value);
    };

    setFilter(columnId, filters);
    dispatch({ type: columnId, payload: filters });
  };

  const handleDateToChange = (columnId, colPart, from, date) => {
    dispatch({ type: colPart, payload: date });
    setFilter(columnId, [from, date]);
  };

  const handleDateChange = (columnId, colPart, colToPart, date) => {
    dispatch({ type: colPart, payload: date });
    if (colToPart != null) {
      dispatch({ type: colToPart, payload: null });
      setFilter(columnId, "");
    }
    setFilter(columnId, [date]);
  };

  const handleMultiChange = (selOptions, field) => {
    dispatch({ type: field, payload: selOptions });
    const values = selOptions.map((item) => item.value);
    setFilter(field, values);
    // let filteredItems = notes;
    // filteredItems = selOptions.length
    //   ? notes.filter((item) => {
    //       return values.includes(item[field]);
    //     })
    //   : notes;
    // updateFilterData(filteredItems, field);
  };
  const handleClearFilters = () => {
    setAllFilters([]);
    dispatch({ type: "RESET_FILTERS" });
    setGlobalFilter("");
    //setFilterData(notes);
  };

  const filterData = [
    {
      accessor: "id",
      label: "ID",
      type: "id",
      data: noteIds,
      control: "select",
      name: "filtersId",
      placeholder: "Select Note Id/s...",
      styles: finderSelectStyles,
      value: id,
    },
    {
      accessor: "title",
      label: "Title",
      type: "title",
      data: titles,
      control: "select",
      name: "filtersHeader",
      placeholder: "Select Title/s...",
      styles: finderSelectStyles,
      value: title,
    },

    {
      accessor: "comment",
      label: "Comment",
      type: "comment",
      data: comments,
      control: "select",
      name: "filtersComment",
      placeholder: "Select Comment/s...",
      styles: finderSelectStyles,
      value: comment,
    },
    {
      accessor: "report",
      label: "Report",
      type: "report",
      data: reports,
      control: "select",
      name: "filtersReport",
      placeholder: "Select Report/s...",
      styles: finderSelectStyles,
      value: report,
    },
    {
      accessor: "lastModifiedDate",
      label: "Date Modified",
      type: "isOpenFrom",
      control: "date",
      start: modFrom,
      startString: "modFrom",
      end: modTo,
      endString: "modTo",
    },
  ];

  return (
    <div className={finderStyles.container}>
      <div className={finderStyles.gridContainer}>
        <div className={finderStyles.leftSection}>
          <div
            className={finderStyles.clearFiltersContainer}
            onClick={handleClearFilters}
          >
            <BiReset className={finderStyles.clearFiltersIcon} />
            <span className={finderStyles.clearFiltersTextComment}>
              Reset Filters
            </span>
          </div>
          <div className={finderStyles.filterSection}>
            {filterData.map((filter) => {
              return (
                <div className={finderStyles.filter} key={filter.accessor}>
                  {columns.find((column) => column.accessor === filter.accessor)
                    ?.show ? (
                    <>
                      {isOpen[filter.type] ? (
                        <RiArrowDownSLine
                          className={finderStyles.iconFilter}
                          onClick={() =>
                            dispatch({
                              type: "SET_IS_OPEN",
                              payload: { type: filter.type, value: false },
                            })
                          }
                        />
                      ) : (
                        <RiArrowRightSLine
                          className={finderStyles.iconFilter}
                          onClick={() =>
                            dispatch({
                              type: "SET_IS_OPEN",
                              payload: { type: filter.type, value: true },
                            })
                          }
                        />
                      )}
                      <label className={finderStyles.lblFilter}>
                        {filter.label}
                      </label>
                    </>
                  ) : (
                    <>
                      <RiArrowRightSLine
                        className={finderStyles.iconFilterHide}
                      />
                      <label className={finderStyles.lblFilterDisabled}>
                        {filter.label}
                      </label>
                    </>
                  )}

                  {isOpen[filter.type] ? (
                    <div className={finderStyles.filterContent}>
                      {filter.control === "check" &&
                        filter.data.map((val) => (
                          <Form.Check
                            type="checkbox"
                            label={val}
                            value={val}
                            id={val}
                            onClick={(e) => handleSelect(filter.accessor, e)}
                          />
                        ))}
                      {filter.control === "date" && (
                        <div className={finderStyles.datepickerWrapper}>
                          <DatePicker
                            selected={filter.start}
                            onChange={(date) =>
                              handleDateChange(
                                filter.accessor,
                                filter.startString,
                                filter.endString,
                                date
                              )
                            }
                            className={finderStyles.datePicker}
                            dateFormat="dd/MM/y"
                            placeholderText="Pick date..."
                          />

                          <label>To</label>
                          <DatePicker
                            selected={filter.end}
                            onChange={(date) =>
                              handleDateToChange(
                                filter.accessor,
                                filter.endString,
                                filter.start,
                                date
                              )
                            }
                            minDate={filter.start}
                            dateFormat="dd/MM/y"
                            placeholderText="Pick date..."
                            disabled={filter.start === null}
                            className={finderStyles.datePicker}
                          />
                        </div>
                      )}
                      {filter.control === "select" && (
                        <Select
                          name={filter.name}
                          placeholder={filter.placeHolder}
                          styles={filter.styles}
                          value={filter.value}
                          options={filter.data}
                          onChange={(selOptions) =>
                            handleMultiChange(selOptions, filter.accessor)
                          }
                          isMulti
                        />
                      )}
                    </div>
                  ) : columns.find(
                      (column) => column.accessor === filter.accessor
                    )?.show ? (
                    <VisibilityOn
                      onClick={() => handleHide(filter.accessor)}
                      className={finderStyles.iconColsShow}
                    />
                  ) : (
                    <VisibilityOff
                      onClick={() => handleHide(filter.accessor)}
                      className={finderStyles.iconColsHide}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className={finderStyles.rightSection}>
          <div className={finderStyles.topSection}>
            <FindInPageTwoToneIcon className={finderStyles.finderIconNote} />
            <label className={finderStyles.topHeaderNote}>Note Finder</label>
            <div className={finderStyles.searchBox}>
              <Form.Control
                type="text"
                placeholder=""
                value={globalFilter || ""}
                onChange={handleSearch}
                className={finderStyles.searchControl}
              />
            </div>
            <Button
              startIcon={<IosShare className={finderStyles.buttonIcon} />}
              variant="contained"
              onClick={() =>
                exportToExcel(columnMap, tableInstance, rows, "Note")
              }
              className={finderStyles.button}
            >
              Export to Excel
            </Button>
          </div>
          <div className={finderStyles.countSectionNote}>
            {rows.length === 1
              ? `${rows.length} Result`
              : `${rows.length} Results`}
          </div>

          <div className={finderStyles.bottomSection}>
            <table {...getTableProps()} className={finderStyles.grid}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        <CustomTooltip title="Click to sort">
                          {column.render("Header")}
                        </CustomTooltip>
                        {/* <div
                          {...column.getResizerProps()}
                          className={`resizer ${
                            column.isResizing ? "isResizing" : ""
                          }`}
                        /> */}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <HiSortDescending
                              className={finderStyles.sortIcon}
                            />
                          ) : (
                            <HiSortAscending
                              className={finderStyles.sortIcon}
                            />
                          )
                        ) : (
                          ""
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              {notes.length > 0 ? (
                <tbody
                  className={finderStyles.reportContent}
                  {...getTableBodyProps()}
                >
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              ) : (
                <div className={finderStyles.loaderOverlay}>
                  <div className={finderStyles.loaderContainer}>
                    <ThreeDots color="#186d90" height={150} width={150} />
                  </div>
                </div>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
